import React from 'react';
import {useIntl} from 'react-intl';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import {anchorElementId, novaInstanceKey, NovaAgentPicker, type NovaAgentPickerAlign} from './NovaAgentPicker';
import {showChangeAgentConfirmationModal} from './modals/ChangeAgentConfirmationModal';
import {NovaCompactButton} from './NovaCompactButton';

// Changing the agent is two separate things that only share a name, and they are kept apart here
// because they belong in different places on screen and have different lifetimes.
//
// The list is the long-lived half. The task modal's header caret opens it - that is where the
// design puts every agent list in this modal - so it has to be mounted whenever the header might
// offer that caret: while the thread is folded, while a run is in flight, on a turn that failed.
// Mounting it beside the button meant it came and went with whatever card row happened to hold the
// button, and a caret aimed at a list that is not mounted does nothing at all.
//
// The button is the short-lived half. It belongs in one card's action row, and only on a turn that
// has an answer to run a different agent against.

export interface ChangeAgentPickerProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx). The key the trigger below and the
	// modal header's caret both toggle.
	instanceKey?: string;
	// See NovaAgentPickerProps.anchorInstanceKey: which control the list hangs from, when that is
	// not the trigger. The design opens it under the task modal's own Action/Continue control.
	anchorInstanceKey?: string;
	// How the list lines up with that control — 'right' where it sits at the edge of the modal.
	align?: NovaAgentPickerAlign;
	currentAgentId: string;
	hasReplies: boolean;
	// Whether this person may act on the agent at all. Checked here rather than only on the button:
	// the list opens from the modal header's caret as well, so a guard that lived on the button
	// would be no guard at all for anyone who reached the list the other way.
	canAct?: boolean;
	onAgentChanged: (newAgentId: string) => void;
}

export const ChangeAgentPicker = (props: ChangeAgentPickerProps): JSX.Element => {
	const handleSelect = (selectedAgentId: string): void => {
		if (props.canAct === false) return;
		if (selectedAgentId === props.currentAgentId) return;
		if (props.hasReplies) {
			showChangeAgentConfirmationModal({onConfirm: () => props.onAgentChanged(selectedAgentId)});
		} else {
			props.onAgentChanged(selectedAgentId);
		}
	};

	return (
		<NovaAgentPicker
			taskId={props.taskId}
			instanceKey={props.instanceKey}
			anchorInstanceKey={props.anchorInstanceKey}
			align={props.align}
			onSelectAgent={handleSelect}
		/>
	);
};

export interface ChangeAgentTriggerProps {
	taskId: string;
	// The key this opens. Must match the picker's own, which is what listens for the toggle.
	instanceKey?: string;
	// Shown, but refused, with the reason on it — changing the agent starts a fresh run, so it
	// needs the same permission acting on the task needs. The picker refuses on the same question;
	// this only keeps a control that cannot act from looking like one that can.
	disabled?: boolean;
	disabledReason?: string;
}

export const ChangeAgentTrigger = (props: ChangeAgentTriggerProps): JSX.Element => {
	const {formatMessage} = useIntl();

	return (
		// The same compact outlined control the answer card's other actions use, in its neutral
		// tone: changing the agent is not the card's primary act, and a text link among three
		// bordered buttons reads as a different kind of thing than it is.
		<NovaCompactButton
			tone="neutral"
			onPress={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
			disabled={props.disabled}
			title={props.disabled ? props.disabledReason : undefined}
			data-cy="nova-change-agent"
			id={anchorElementId(props.taskId, props.instanceKey)}
		>
			{formatMessage({id: 'nova_invocation.change_agent.button'})}
		</NovaCompactButton>
	);
};
